import { Button, Icon, styled, theme } from 'design-system';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import { Check, Journal } from '../../../resources/journal';

interface ChecklistPopupProps {
  journal: Journal;
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'technical' | 'language';
}

type CheckItem = {
  critical: boolean;
  checkKey: string;
  label: string;
  content: {
    text: string;
    links: {
      text: string;
      url: string;
    }[];
  };
  groupingLabel: string;
};

export const ChecklistPopup = ({
  journal,
  isOpen,
  onClose,
  defaultTab,
}: ChecklistPopupProps) => {
  const { t } = useTranslation(['common']);
  const [languageChecks, setLanguageChecks] = useState<Check[]>([]);
  const [technicalChecks, setTechnicalChecks] = useState<Check[]>([]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'technical' | 'language'>(
    'technical',
  );

  useEffect(() => {
    if (journal.checklist.checks) {
      const langChecks = Object.values(journal.checklist.checks).filter(
        (item) => item.grouping === 'language',
      );
      const techChecks = Object.values(journal.checklist.checks).filter(
        (item) => item.grouping !== 'language',
      );

      setLanguageChecks(langChecks);
      setTechnicalChecks(techChecks);

      // Set tab based on defaultTab prop or fallback to default logic
      if (defaultTab) {
        if (defaultTab === 'language' && langChecks.length > 0) {
          setActiveTab('language');
        } else if (defaultTab === 'technical' && techChecks.length > 0) {
          setActiveTab('technical');
        } else {
          // Fallback if defaultTab doesn't have available checks
          if (techChecks.length > 0) {
            setActiveTab('technical');
          } else if (langChecks.length > 0) {
            setActiveTab('language');
          }
        }
      } else {
        // Default behavior when no defaultTab is specified
        if (techChecks.length > 0) {
          setActiveTab('technical');
        } else if (langChecks.length > 0) {
          setActiveTab('language');
        }
      }
    }
  }, [journal.checklist.checks, defaultTab]);

  const toggleExpanded = (checkKey: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(checkKey)) {
      newExpanded.delete(checkKey);
    } else {
      newExpanded.add(checkKey);
    }
    setExpandedItems(newExpanded);
  };

  const renderCheckItem = (item: Check) => (
    <CheckItem
      key={item.checkKey}
      data-test-id={`checklist-popup-check-${item.checkKey}`}
    >
      <CheckItemHeader onClick={() => toggleExpanded(item.checkKey)}>
        <ExpandButton data-test-id={`checklist-popup-expand-${item.checkKey}`}>
          {expandedItems.has(item.checkKey) ? (
            <Icon name={'x'} variant="solid" width={8} />
          ) : (
            <Icon name={'Plus'} variant="solid" width={8} />
          )}
        </ExpandButton>
        <CheckItemLabel>
          <div dangerouslySetInnerHTML={{ __html: item.label }} />
          {item.critical && !expandedItems.has(item.checkKey) && (
            <CriticalIcon>
              <Icon name={'TriangleExclamation'} variant="solid" width={14} />
            </CriticalIcon>
          )}
        </CheckItemLabel>
      </CheckItemHeader>
      {expandedItems.has(item.checkKey) && (
        <CheckItemContent>
          {item.critical && (
            <CriticalStatement>
              <CriticalIcon>
                <Icon name={'TriangleExclamation'} variant="solid" width={14} />
              </CriticalIcon>
              <CriticalText>
                [Marked as a critical check by journal editor] -{' '}
              </CriticalText>
            </CriticalStatement>
          )}
          <div dangerouslySetInnerHTML={{ __html: item.content?.text }} />
        </CheckItemContent>
      )}
    </CheckItem>
  );

  if (!isOpen) {
    return null;
  }

  return (
    <PopupOverlay onClick={onClose}>
      <PopupContainer
        data-test-id="checklist-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupHeader>
          <PopupTitle>
            Recommended checks for{' '}
            {journal.label.length > 45
              ? `${journal.label.slice(0, 45)}...`
              : journal.label}
          </PopupTitle>
          <div data-test-id="checklist-popup-close-btn" onClick={onClose}>
            <Icon name={'x'} variant="solid" width={24} />
          </div>
        </PopupHeader>

        <TabStrip>
          {languageChecks.length > 0 && (
            <TabButton
              data-test-id="checklist-popup-language-tab"
              active={activeTab === 'language'}
              onClick={() => setActiveTab('language')}
            >
              <TabIcon active={activeTab === 'language'}>
                <Image
                  src="/language-new.svg"
                  alt="Language Quality"
                  width={20}
                  height={20}
                />
              </TabIcon>
              {t('language_quality', 'Language Quality')}
            </TabButton>
          )}
          {technicalChecks.length > 0 && (
            <TabButton
              data-test-id="checklist-popup-technical-tab"
              active={activeTab === 'technical'}
              onClick={() => setActiveTab('technical')}
            >
              <TabIcon active={activeTab === 'technical'}>
                <Image
                  src="/technical-new.svg"
                  alt="Technical Checks"
                  width={16}
                  height={16}
                />
              </TabIcon>
              {t('technical_checks', 'Technical Checks')}
            </TabButton>
          )}
        </TabStrip>

        <PopupContent>
          <ChecksSection>
            {activeTab === 'technical' && technicalChecks.length > 0 && (
              <CheckColumn>
                <ChecksList>{technicalChecks.map(renderCheckItem)}</ChecksList>
              </CheckColumn>
            )}
            {activeTab === 'language' && languageChecks.length > 0 && (
              <CheckColumn>
                <ChecksList>{languageChecks.map(renderCheckItem)}</ChecksList>
              </CheckColumn>
            )}
          </ChecksSection>
        </PopupContent>
      </PopupContainer>
    </PopupOverlay>
  );
};

// Styled components
const PopupOverlay = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
});

const PopupContainer = styled('div', {
  width: '720px',
  height: '418px',
  backgroundColor: theme.colors.white,
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const PopupHeader = styled('div', {
  height: '64px',
  backgroundColor: theme.colors.gray3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
});

const PopupTitle = styled('h3', {
  fontFamily: theme.fonts.sansSerif,
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  letterSpacing: '0%',
  color: theme.colors.blue12,
  margin: 0,
});

const PopupContent = styled('div', {
  flex: 1,
  padding: '20px',
  overflow: 'auto',
});

const ChecksSection = styled('div', {
  display: 'flex',
  gap: '20px',
  height: '100%',
});

const CheckColumn = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const TabStrip = styled('div', {
  display: 'flex',
  borderBottom: `1px solid ${theme.colors.gray3}`,
  backgroundColor: theme.colors.white,
  padding: '0 10px',
  gap: '20px',
});

const TabButton = styled('button', {
  width: '200px',
  height: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '16px 16px',
  border: 'none',
  backgroundColor: 'transparent',
  fontSize: '14px',
  fontWeight: 500,
  color: theme.colors.blue12,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  position: 'relative',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '202px',
    height: '6px',
    backgroundColor: 'transparent',
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
    opacity: 0,
    transition: 'all 0.2s ease',
  },

  variants: {
    active: {
      true: {
        color: theme.colors.blue10,
        backgroundColor: theme.colors.blue1,

        '&::after': {
          backgroundColor: theme.colors.blue9,
          opacity: 1,
        },

        // Active tab should remain blue on hover
        '&:hover': {
          color: theme.colors.blue10,
          backgroundColor: theme.colors.blue1,
        },
      },
      false: {
        // Inactive tabs should be black normally, blue on hover
        color: theme.colors.gray12,

        '&:hover': {
          color: theme.colors.blue10,
          backgroundColor: theme.colors.gray1,
        },
      },
    },
  },
});

const TabIcon = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    active: {
      true: {
        // Blue color filter for active state - stays blue on hover too
        '& img': {
          filter:
            'brightness(0) saturate(100%) invert(26%) sepia(87%) saturate(1352%) hue-rotate(213deg) brightness(99%) contrast(97%)',
          transition: 'filter 0.2s ease',
        },
      },
      false: {
        // Black color for inactive state by default
        '& img': {
          filter:
            'brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)',
          transition: 'filter 0.2s ease',
        },

        // Parent button hover will change icon to blue
        'button:hover &': {
          '& img': {
            filter:
              'brightness(0) saturate(100%) invert(26%) sepia(87%) saturate(1352%) hue-rotate(213deg) brightness(99%) contrast(97%)',
          },
        },
      },
    },
  },
});

const ChecksList = styled('div', {
  flex: 1,
  overflow: 'auto',
});

const CheckItem = styled('div', {
  marginBottom: '8px',
  borderRadius: '8px',
  overflow: 'hidden',
});

const CheckItemHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  padding: '12px',
  cursor: 'pointer',
  backgroundColor: theme.colors.white,
  '&:hover': {
    backgroundColor: theme.colors.gray1,
  },
});

const ExpandButton = styled(Button, {
  backgroundColor: 'transparent',
  border: `1px solid ${theme.colors.blue12}`,
  borderRadius: '50%',
  width: '16px',
  height: '16px',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '8px',
  color: theme.colors.blue12,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&:active': {
    backgroundColor: 'transparent',
  },
  '&:focus': {
    backgroundColor: 'transparent',
  },
});

const CheckItemLabel = styled('div', {
  flex: 1,
  fontSize: '14px',
  fontWeight: 500,
  color: theme.colors.blue12,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

const CriticalIcon = styled('div', {
  color: theme.colors.gray10,
});

const CheckItemContent = styled('div', {
  padding: '12px',
  paddingTop: '0',
  fontSize: '14px',
  lineHeight: '20px',
  color: theme.colors.blue12,
  backgroundColor: theme.colors.gray1,
});

const CriticalStatement = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '4px',
  marginBottom: '8px',
});

const CriticalText = styled('span', {
  fontStyle: 'italic',
  fontSize: '13px',
});
